/**
 * Read-only "View as agent" client state (D2-SEC-011, #3944).
 *
 * A super-user or manager can see the CRM exactly as one agent sees it. The
 * server mints a short-lived signed token (`POST /auth/view-as/start/`); while
 * it is held here every same-origin `/api/` call carries it in the
 * `X-View-As` header next to the viewer's OWN access token, and the server
 * answers as the agent -- read only. Nothing here is a security boundary: the
 * server refuses every write and every sensitive read while the header is
 * present, and refuses the header itself once the session ends or expires.
 *
 * The token lives in sessionStorage (this tab only, gone when it closes). It is
 * attached by a single `fetch` wrapper so the many raw `fetch()` call sites
 * (downloads, uploads, feedback, ...) are covered without touching each one:
 * a call that went out WITHOUT the header would answer as the viewer, which is
 * exactly the confusion this feature exists to remove.
 */

export const VIEW_AS_STORAGE_KEY = "lwp_view_as";
export const VIEW_AS_HEADER = "X-View-As";
export const VIEW_AS_STATUS_HEADER = "X-View-As-Status";

export interface ViewAsPerson {
  id: number;
  name: string;
}

export interface ViewAsState {
  token: string;
  expires_at: string;
  viewer: ViewAsPerson;
  target: ViewAsPerson;
}

/** Calls that must always go out as the viewer: the session plumbing itself. */
const NEVER_TAGGED = [
  "/api/v1/auth/login/",
  "/api/v1/auth/refresh/",
  "/api/v1/auth/logout/",
];

function isViewAsState(value: unknown): value is ViewAsState {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.token === "string" &&
    typeof v.expires_at === "string" &&
    !!v.target &&
    !!v.viewer
  );
}

/** The active view-as state, or null (also null once it has expired). */
export function getViewAs(): ViewAsState | null {
  try {
    const raw = sessionStorage.getItem(VIEW_AS_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isViewAsState(parsed)) return null;
    if (Date.parse(parsed.expires_at) <= Date.now()) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setViewAs(state: ViewAsState): void {
  try {
    sessionStorage.setItem(VIEW_AS_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore -- without storage the session simply does not start.
  }
}

export function clearViewAs(): void {
  try {
    sessionStorage.removeItem(VIEW_AS_STORAGE_KEY);
  } catch {
    // ignore.
  }
}

/** The raw token for the stop call, even if it has expired locally. */
export function getStoredViewAsToken(): string | null {
  try {
    const raw = sessionStorage.getItem(VIEW_AS_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isViewAsState(parsed) ? parsed.token : null;
  } catch {
    return null;
  }
}

/** Should this request carry the view-as header? Same-origin `/api/` only. */
export function shouldTag(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url, window.location.origin);
  } catch {
    return false;
  }
  if (parsed.origin !== window.location.origin) return false;
  if (!parsed.pathname.startsWith("/api/")) return false;
  return !NEVER_TAGGED.includes(parsed.pathname);
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

/** Leave view-as and reload as the viewer (used on exit and on a dead token). */
export function leaveViewAs(destination = "/"): void {
  clearViewAs();
  window.location.assign(destination);
}

/**
 * Window event fired when the server refuses a write because the session is
 * viewing-as (D2-SEC-011). The banner turns it into ONE non-destructive toast,
 * so a refused write never needs a page to swap its content for an error box.
 */
export const VIEW_AS_REFUSED_EVENT = "lwp:view-as-refused";

const SAFE_METHODS = ["GET", "HEAD", "OPTIONS"];

/** The server's view-as refusal codes (`apps/users/view_as.py::_refuse`). */
function isViewAsRefusalCode(code: unknown): boolean {
  return typeof code === "string" && code.startsWith("view_as_");
}

/**
 * Was this error the server refusing an action because the session is
 * viewing-as? Duck-typed on `{status, data.code}` (the `ApiError` shape) so this
 * module stays free of an import cycle with `api/client.ts`.
 */
export function isViewAsRefusal(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { status?: unknown; data?: { code?: unknown } | null };
  return e.status === 403 && isViewAsRefusalCode(e.data?.code);
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (typeof Request !== "undefined" && input instanceof Request) {
    return input.method.toUpperCase();
  }
  return "GET";
}

/** Peek a refused write's body (via a clone) and announce it, never throwing. */
async function announceRefusedWrite(response: Response): Promise<void> {
  try {
    const data: unknown = await response.clone().json();
    const code = (data as { code?: unknown } | null)?.code;
    if (isViewAsRefusalCode(code)) {
      window.dispatchEvent(
        new CustomEvent(VIEW_AS_REFUSED_EVENT, { detail: { code } }),
      );
    }
  } catch {
    // Not JSON / no clone -- the caller still gets the 403 as normal.
  }
}

let installed = false;

/**
 * Wrap `window.fetch` once so every same-origin `/api/` call made while viewing
 * carries the token, and a server verdict that the session is dead drops it and
 * reloads as the viewer (never a silent fall-back to the viewer's own data).
 */
export function installViewAsFetch(): void {
  if (installed || typeof window === "undefined" || !window.fetch) return;
  installed = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const state = getStoredViewAsToken();
    if (!state || !shouldTag(requestUrl(input))) {
      return originalFetch(input, init);
    }
    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    headers.set(VIEW_AS_HEADER, state);
    const response = await originalFetch(input, { ...init, headers });
    if (response.headers.get(VIEW_AS_STATUS_HEADER) === "invalid") {
      leaveViewAs("/");
    } else if (
      response.status === 403 &&
      !SAFE_METHODS.includes(requestMethod(input, init))
    ) {
      void announceRefusedWrite(response);
    }
    return response;
  };
}
